import React, { useState } from "react";
import { Button, Modal, Form, Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faPlay, 
    faCheckCircle, 
    faExclamationTriangle,
    faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import keepAliveInstanceApi from "../../api/keepAliveInstanceApi";

/**
 * KeepAliveInstance Component
 * 
 * This component provides a "Keep Alive" button and modal for a single instance.
 * It handles the keep-alive process with proper error handling and user feedback.
 */
const KeepAliveInstance = ({ 
    instanceId, 
    environment, 
    eventId, 
    onKeepAliveComplete, 
    isCompleted 
}) => {
    // State management
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [ticketId, setTicketId] = useState("");

    // Event handlers
    const handleShow = () => {
        setShow(true);
        setError(null);
        setResult(null);
    };

    const handleClose = () => {
        setShow(false);
        setError(null);
        setResult(null);
    };

    const handleKeepAlive = async () => {
        // Validate ticket ID
        if (!ticketId.trim()) {
            setError("Please enter a ticket ID");
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const response = await keepAliveInstanceApi(
                eventId, 
                environment, 
                [instanceId], 
                ticketId.trim()
            );

            if (response && response.message) {
                setResult(response);
                onKeepAliveComplete(instanceId, true, response.message);
            } else {
                const errorMsg = "Failed to keep instance alive. Please try again.";
                setError(errorMsg);
                onKeepAliveComplete(instanceId, false, errorMsg);
            }
        } catch (err) {
            let errorMsg = "An unexpected error occurred. Please try again.";
            
            if (err.message) {
                if (err.message.includes("already done") || 
                    err.message.includes("ExecutionAlreadyExists") ||
                    err.message.includes("already exists") ||
                    err.message.includes("Keep Alive already done")) {
                    errorMsg = "This instance is already in keep-alive mode for this event and ticket combination.";
                    // Treat this as a success case since it's already done
                    onKeepAliveComplete(instanceId, true, errorMsg);
                    return;
                } else if (err.message.includes("Access denied")) {
                    errorMsg = "Access denied to Step Function. Please check IAM permissions or contact the administrator.";
                } else if (err.message.includes("role not found")) {
                    errorMsg = "Cross-account role not found. Please contact the administrator.";
                } else if (err.message.includes("Invalid parameters")) {
                    errorMsg = "Invalid request parameters. Please check your input.";
                } else if (err.message.includes("Network error")) {
                    errorMsg = "Network error. Please check your connection and try again.";
                } else {
                    errorMsg = err.message;
                }
            }
            
            setError(errorMsg);
            onKeepAliveComplete(instanceId, false, errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Render completed state
    if (isCompleted) {
        return (
            <Button 
                variant="success" 
                size="sm" 
                disabled 
                className="d-flex align-items-center"
            >
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                Keep Alive Done
            </Button>
        );
    }

    // Render active keep alive button
    return (
        <>
            <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={handleShow} 
                disabled={loading}
                className="d-flex align-items-center"
            >
                {loading ? (
                    <>
                        <Spinner size="sm" className="me-1" />
                        Processing...
                    </>
                ) : (
                    <>
                        <FontAwesomeIcon icon={faPlay} className="me-1" />
                        Keep Alive
                    </>
                )}
            </Button>

            <Modal show={show} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faPlay} className="me-2 text-primary" />
                        Keep Instance Alive
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Instance ID</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={instanceId} 
                                readOnly 
                                className="bg-light"
                            />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Environment</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={environment} 
                                readOnly 
                                className="bg-light"
                            />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Event ID</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={eventId} 
                                readOnly 
                                className="bg-light"
                            />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Ops Ticket ID <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control 
                                type="text" 
                                value={ticketId}
                                onChange={(e) => setTicketId(e.target.value)}
                                placeholder="Enter ticket ID (e.g., OPS-1234)"
                                required
                            />
                            <Form.Text className="text-muted">
                                Enter the ticket ID for tracking this keep-alive request
                            </Form.Text>
                        </Form.Group>
                    </Form>

                    {error && (
                        <Alert variant="danger" className="mt-3">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <strong>Error:</strong> {error}
                        </Alert>
                    )}

                    {result && (
                        <Alert 
                            variant={result.alreadyExists ? "info" : "success"} 
                            className="mt-3"
                        >
                            <FontAwesomeIcon 
                                icon={result.alreadyExists ? faInfoCircle : faCheckCircle} 
                                className="me-2" 
                            />
                            <strong>
                                {result.alreadyExists ? "Info:" : "Success!"}
                            </strong> {result.message}
                            
                            {result.executionArn && (
                                <div className="mt-2">
                                    <small>
                                        <strong>Execution ARN:</strong> {result.executionArn}
                                    </small>
                                </div>
                            )}
                            
                            {result.alreadyExists && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        This instance is already in keep-alive mode for this event and ticket combination.
                                    </small>
                                </div>
                            )}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleKeepAlive} 
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faPlay} className="me-2" />
                                Keep Alive
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default KeepAliveInstance;
